import React from 'react'
import PropTypes from 'prop-types'
import { Icon } from 'semantic-ui-react'

import './CompactModeSection.css'

/**
 * Lightweight collapsible section divider for Compact Mode grouping.
 * Controlled component: the parent owns the collapsed state.
 *
 * Props:
 *   groupKey     {string}     Unique key identifying the group
 *   label        {string}     Display label for the section divider
 *   count        {number}     Number of figures in this section
 *   collapsed    {boolean}    Whether the section is currently collapsed
 *   onToggle     {Function}   Callback when the divider is clicked. Firma: fn(groupKey)
 *   children     {ReactNode}  The content to render inside
 *   nested       {boolean}    [optional] Whether this is a nested (child) section. Default: false
 */
const CompactModeSection = ({ groupKey, label, count, collapsed, onToggle, children, nested = false }) => {
  const isOpen = !collapsed

  return (
    <div className={nested ? 'compact-section-nested' : 'compact-section'}>
      <div
        className={nested ? 'compact-divider-nested' : 'compact-divider'}
        onClick={() => onToggle(groupKey)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(groupKey) }}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse ' + label : 'Expand ' + label}
      >
        <span className="compact-divider-label">
          {label}
          <span className="compact-divider-count">({count})</span>
          <Icon name={isOpen ? 'chevron down' : 'chevron right'} className="compact-divider-chevron" />
        </span>
        <span className="compact-divider-line" />
      </div>
      {isOpen && children}
    </div>
  )
}

CompactModeSection.propTypes = {
  groupKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  collapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  nested: PropTypes.bool,
}

export default CompactModeSection
